import React, { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import { ListTableQueryPagination } from "../../../components/ListTable/ListTableQueyPagination";
import { adminApiService } from "../../../../utils/api";
import { GfFigmaTemplateData, MuiFilterRequestDto } from "@swagger/typescript-fetch-goflyer";
import { TableData } from "../../../../utils/interface/tableData";
import { useFigmaTemplateDataTableColumns } from "./useFigmaTemplateDataTableColumns";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { AccountCardWrapper } from "../../../components/AccountCardWrapper";
import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { BlackFontTransparentButton } from "../../../../utils/commonStyledComponents/commonMuiStyled";
import DeleteIcon from "@mui/icons-material/Delete";
import { PermissionComponentWrapper } from "../../../components/PermissionComponentWrapper";
import { useNavigate } from "react-router-dom";
import { MuiConfirmationModal } from "../../../components/MuiConfirmationModal";
import { toastError, toastSuccess } from "../../../../utils/pageUtil/toast";
import { SetupPageTopAppBar } from "../../../../utils/commonStyledComponents/commonStyled";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { routes } from "../../../../utils/constant/navigationRoutes";
import { muiFilterModelHasActiveItems } from "../../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../../utils/guardedListFetch";

const FigmaTemplateDataList: React.FC = () => {
  const columns = useFigmaTemplateDataTableColumns();
  const { paginationModel, sortModel, handleTableChange } = useQueryParams();
  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [listData, setListData] = useState<TableData<GfFigmaTemplateData>>();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const fetchListWithFilter = async (activeFilterModel: GridFilterModel) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfFigmaTemplateDataAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.getManyBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
          undefined,
          undefined,
          undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : ["updatedAt,DESC"],
          ["figmaTemplateFiles"],
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        ),
      (response) => {
        setListData((prev) => ({
          ...prev,
          rows: response.data,
          total: response.total,
        }));
      },
    );
  };

  useEffect(() => {
    setListData({ columns });
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchListWithFilter(filterModel);
    } else {
      fetchList();
    }
  }, [paginationModel, sortModel, filterModel]);

  const handleOpenDelete = () => {
    setOpenDelete(true);
  };

  const handleCancelDelete = () => {
    setOpenDelete(false);
  };

  const handleConfirmDelete = async () => {
    if (!rowSelectionModel?.length) return;
    try {
      setPaginationLoading(true);
      const promises = rowSelectionModel.map((id) =>
        adminApiService.deleteOneBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
          id as string,
          authHeaders,
        ),
      );
      await Promise.all(promises);
      toastSuccess(t(messages.Successfully_Deleted()));
      setRowSelectionModel([]);
      if (muiFilterModelHasActiveItems(filterModel)) {
        fetchListWithFilter(filterModel);
      } else {
        fetchList();
      }
    } catch (e) {
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setPaginationLoading(false);
      handleCancelDelete();
    }
  };

  const handleCreate = () => {
    navigate(routes.figmaTemplate.create);
  };

  return (
    <Box
      sx={{ marginTop: "65px" }}
      data-testid="figma-template-data-list-container"
    >
      <AccountCardWrapper title={"Figma Template Data"}>
        <Box data-testid="figma-template-data-content">
          <SetupPageTopAppBar>
            <Stack direction="row" spacing={2}>
              <PermissionComponentWrapper
                requiredPermission={["create"]}
                requiredPermissionGroup={["figmaTemplates"]}
              >
                <BlackFontTransparentButton
                  variant="text"
                  startIcon={
                    <UploadFileIcon
                      style={{ color: "#ff8038", fontSize: 25 }}
                    />
                  }
                  onClick={handleCreate}
                  data-testid="create-button"
                >
                  {t(messages.Create())}
                </BlackFontTransparentButton>
              </PermissionComponentWrapper>
              <PermissionComponentWrapper
                requiredPermission={["delete"]}
                requiredPermissionGroup={["figmaTemplates"]}
              >
                <BlackFontTransparentButton
                  variant="text"
                  startIcon={
                    <DeleteIcon
                      style={{
                        color: "#ff8038",
                        fontSize: 25,
                        opacity: !rowSelectionModel?.length ? 0.5 : 1,
                      }}
                    />
                  }
                  disabled={!rowSelectionModel?.length}
                  onClick={handleOpenDelete}
                  data-testid="delete-button"
                  sx={{ textTransform: "capitalize" }}
                >
                  {t(messages.Delete())}
                </BlackFontTransparentButton>
              </PermissionComponentWrapper>
            </Stack>
          </SetupPageTopAppBar>
          <ListTableQueryPagination
            loadFailed={listRequestGuard.loadFailed}
            columns={columns}
            rows={listData?.rows}
            total={paginationLoading ? 0 : listData?.total}
            paginationModel={paginationModel}
            sortModel={sortModel}
            isLoading={paginationLoading}
            onTableChange={handleTableChange}
            dynamicHeightAdjuster={230}
            handleRowSelection={(value) => setRowSelectionModel(value)}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
          />
        </Box>
      </AccountCardWrapper>
      <MuiConfirmationModal
        open={openDelete}
        handleCancel={handleCancelDelete}
        handleConfirm={handleConfirmDelete}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Templates(),
          { count: rowSelectionModel.length },
        )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
      />
    </Box>
  );
};

export default FigmaTemplateDataList;
